/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */

import React, { useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, type XCSS, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ContentContainer } from '../../../../common/ui/content-container';
import { ErrorBoundary } from '../../../../controllers/error-boundary';

type HeaderProps = {
	components: {
		feedback: JSX.Element;
		filters: JSX.Element;
		input: JSX.Element;
	};
	disableHeaderGridStyles?: boolean;
};

const baseHeaderStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	width: '100%',
});

const headerGridStyles = xcss({
	paddingBottom: 'space.200',
	marginBottom: 'space.250',
	borderBottom: `solid ${token('border.width')} ${token('color.border')}`,
});

const filtersGridAreaStyles = xcss({
	gridArea: 'filters',
});

const Header = ({ components, disableHeaderGridStyles }: HeaderProps) => {
	const { filters } = components;
	const [stickyHeaderStyles, setStickyHeaderStyles] = useState<XCSS>();

	const referenceRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);

	// Use the reference element's position to set the sticky styles on the
	// header. This allows `top` to always be correct.
	useEffect(() => {
		if (!referenceRef.current) {
			return;
		}

		// QS-5148
		// referenceY is a measure of how far the top of search-page is from the top of the scrolling context. This works in old nav, because the scroll matches the viewport
		// In Nav 4, the scroll is the content region and the nav is outside of it
		// This is a temporary fix to unblock Nav4 dogfooding
		let referenceY = referenceRef.current.getBoundingClientRect().top;

		// If the Nav4 feature prop is enabled, we set the referenceY to 0 as the layout doesn't require an offset
		if (true) {
			referenceY = 0;
		}

		const scrollY = window.scrollY;
		const top = referenceY + scrollY;

		setStickyHeaderStyles(
			xcss({
				position: 'sticky',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				top: `${top}px`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				backgroundClip: 'padding-box',
			}),
		);
	}, [setStickyHeaderStyles]);

	return (
		<>
			<Box ref={referenceRef} testId="search-page-header-reference" />
			<Box
				xcss={[baseHeaderStyles, stickyHeaderStyles]}
				testId="search-page-header"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ zIndex: 1 }} // The smallest z-index from xcss options is too big (100)
			>
				<ContentContainer>
					<Grid
						ref={headerRef}
						xcss={disableHeaderGridStyles ? undefined : headerGridStyles}
						columnGap="space.250"
						rowGap="space.200"
						templateAreas={['input feedback', 'filters filters']}
						templateColumns="65fr 35fr"
					>
						<Box xcss={filtersGridAreaStyles}>{filters}</Box>
					</Grid>
				</ContentContainer>
			</Box>
		</>
	);
};

const HeaderWithErrorBoundary = (props: HeaderProps) => {
	return (
		<ErrorBoundary
			fallback={
				<Box xcss={props.disableHeaderGridStyles ? undefined : headerGridStyles}>
					<ContentContainer>{props.components.input}</ContentContainer>
				</Box>
			}
		>
			<Header {...props} />
		</ErrorBoundary>
	);
};

export default HeaderWithErrorBoundary;
